import { type ReduxApplicationState } from 'src/redux/combinedReducer';
import { type Facet } from 'src/redux/productsQuery/types/Facet';
import { type TaxonomyAttribute } from 'src/redux/productsQuery/types/ProductsQueryState';
import { type SortOption } from 'src/redux/productsQuery/types/SortOption';
import { serializeFilterPath } from '../builders/serializeFilterPath';
import { getListingsSortAsURLSearchParams } from '../listings-api/getListingsSortAsURLSearchParams';

type StoreToPageURLState = Pick<ReduxApplicationState, 'productsQuery'>;

export type BuildListingsQueryParamsOptions = {
  page?: number;
  sortOption?: SortOption | null;
};

/**
 * Strips facets whose `key` is registered as taxonomy in `productsQuery.taxonomyAttributeAliases`.
 * Taxonomy lives in the pathname (e.g. `/shop/all/accessories/bags`) and must not also appear
 * in the `/-/...` filter segment. On routes that don't set `taxonomyAttributeAliases` this is a no-op.
 */
const stripTaxonomyFacets = (
  facets: Facet[],
  taxonomyAttributeAliases: TaxonomyAttribute[] | undefined,
): Facet[] => {
  if (!taxonomyAttributeAliases?.length) {
    return facets;
  }
  const taxonomyAliases = new Set(taxonomyAttributeAliases);
  return facets.filter((facet) => !taxonomyAliases.has(facet.alias));
};

export const appendListingsQueryParams = (
  urlParams: URLSearchParams,
  state: StoreToPageURLState,
  options: BuildListingsQueryParamsOptions = {},
): void => {
  const {
    productsQuery: { searchTerm, sortOptions = [] },
  } = state;
  const { page, sortOption = undefined } = options;
  const activeSortOption =
    sortOption === undefined ? sortOptions.find((option) => option.active) : sortOption;

  if (activeSortOption?.key) {
    getListingsSortAsURLSearchParams(urlParams, activeSortOption);
  }

  // don't include page=1 as that is the default
  if (page && page > 1) {
    urlParams.set('page', page.toString());
  }

  if (searchTerm) {
    urlParams.set('term', searchTerm);
  }
};

/**
 * Returns the filter path segment (e.g. "/-/p/-10/c/blue") for the active facets.
 * Returns an empty string when no filters are active.
 * Taxonomy facets (those keyed via `productsQuery.taxonomyAttributeAliases`) are excluded — they live
 * in the pathname (e.g. `/shop/all/accessories/bags`), not the filter segment.
 */
export const buildFilterPathFromState = (state: StoreToPageURLState): string => {
  const { facets, taxonomyAttributeAliases } = state.productsQuery;
  return serializeFilterPath(stripTaxonomyFacets(facets, taxonomyAttributeAliases));
};

/**
 * Returns non-facet query params only (sort, page, term).
 * Facet filters are serialised into the URL path via buildFilterPathFromState.
 */
export const buildListingsQueryParams = (
  state: StoreToPageURLState,
  options: BuildListingsQueryParamsOptions = {},
): URLSearchParams => {
  const urlParams = new URLSearchParams();
  appendListingsQueryParams(urlParams, state, options);
  return urlParams;
};
